import React, { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getAssignedManagers, registerPortalUser } from '../../services/apiclient';
import {
  approveRecruiterRequestAdmin,
  denyRecruiterRequestAdmin,
  getRecruiterRequestsAdmin,
  RecruiterRequestRow,
} from '../../services/recruiterRequestApi';

const RecruiterRequestsPage: React.FC = () => {
  type ApproveForm = {
    username: string;
    email: string;
    phone: string;
    password: string;
    role: string;
    location: string;
    assignedManagerUserId: string;
  };

  const [rows, setRows] = useState<RecruiterRequestRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRecruiter, setSelectedRecruiter] = useState<RecruiterRequestRow | null>(null);
  const [approveTarget, setApproveTarget] = useState<RecruiterRequestRow | null>(null);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [managers, setManagers] = useState<any[]>([]);
  const [managerLoading, setManagerLoading] = useState<boolean>(false);
  const [approveForm, setApproveForm] = useState<ApproveForm>({
    username: '',
    email: '',
    phone: '',
    password: '',
    role: '',
    location: '',
    assignedManagerUserId: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalResults: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getRecruiterRequestsAdmin(pagination.page, pagination.limit, 'PENDING');
      setRows(Array.isArray(response.data) ? response.data : []);
      setPagination((prev) => ({
        ...prev,
        totalPages: response.pagination.totalPages || 1,
        totalResults: response.pagination.totalResults || 0,
      }));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    void load();
  }, [load]);

  const renderValue = (value?: string | null) => (value && value.trim() ? value : '-');
  const formatDate = (date?: string) =>
    date ? date.slice(0, 16).replace('T', ' ') : '-';

  const handlePageChange = (page: number) => {
    if (page < 1 || page > pagination.totalPages || page === pagination.page) return;
    setPagination((prev) => ({ ...prev, page }));
  };

  const getApiRoleParam = (role: string): string | string[] => {
    switch (role) {
      case 'SHM':
      case 'Master BDE':
      case 'BDE':
      case 'FBDE':
      case 'CONSULTANT':
      case 'MODERATOR':
      case 'FREELANCE_COUNSELLOR':
      case 'MASTER_BDE':
        return 'ADMIN';
      case 'HM':
      case 'AGENCY':
        return ['SHM', 'FBDE', 'CONSULTANT'];
      case 'HR':
        return ['HM', 'AGENCY'];
      case 'TPO':
        return ['FBDE', 'CONSULTANT', 'FREELANCE_COUNSELLOR', 'ADMIN'];
      case 'FREELANCE_BDE':
        return ['MASTER_BDE', 'FBDE'];
      case 'BDE_INTERN':
        return ['FREELANCE_BDE'];
      case 'INTERNAL_TEAM':
      case 'COMPANY':
        return 'ADMIN';
      default:
        return role;
    }
  };

  const fetchManagers = useCallback(async (role: string) => {
    if (!role) {
      setManagers([]);
      return;
    }
    setManagerLoading(true);
    try {
      const apiRole = getApiRoleParam(role);
      if (Array.isArray(apiRole)) {
        const responses = await Promise.all(apiRole.map((r) => getAssignedManagers(r)));
        const merged = responses.flat();
        const unique = merged.filter((m, i, self) => i === self.findIndex((x) => x.id === m.id));
        setManagers(unique);
      } else {
        const response = await getAssignedManagers(apiRole);
        setManagers(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      setManagers([]);
      toast.error('Failed to fetch managers');
    } finally {
      setManagerLoading(false);
    }
  }, []);

  useEffect(() => {
    if (approveForm.role) {
      void fetchManagers(approveForm.role);
    } else {
      setManagers([]);
    }
  }, [approveForm.role, fetchManagers]);

  const openApprovePopup = (row: RecruiterRequestRow) => {
    setApproveTarget(row);
    setShowPassword(false);
    setApproveForm({
      username: row.personalDetails?.fullName || '',
      email: row.personalDetails?.email || '',
      phone: row.personalDetails?.phone || '',
      password: '',
      role: '',
      location: [
        row.personalDetails?.permCity || row.personalDetails?.currCity || '',
        row.personalDetails?.permState || row.personalDetails?.currState || '',
      ]
        .filter(Boolean)
        .join(', '),
      assignedManagerUserId: '',
    });
  };

  const handleApproveSubmit = async () => {
    if (!approveTarget) return;
    if (
      !approveForm.username.trim() ||
      !approveForm.email.trim() ||
      !approveForm.password.trim() ||
      !approveForm.role ||
      !approveForm.location.trim() ||
      !approveForm.assignedManagerUserId ||
      !/^\d{10}$/.test(approveForm.phone)
    ) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    setIsActionLoading(true);
    try {
      const registerResponse = await registerPortalUser({
        username: approveForm.username.trim(),
        email: approveForm.email.trim(),
        phone: approveForm.phone.trim(),
        password: approveForm.password,
        location: approveForm.location.trim(),
        role: approveForm.role,
        assignedManagerUserId: approveForm.assignedManagerUserId,
      } as any);

      const createdUserId =
        registerResponse?.data?.user?._id || registerResponse?.data?.user?.id || undefined;
      await approveRecruiterRequestAdmin(approveTarget._id, createdUserId);

      toast.success('Recruiter request approved and user created');
      setApproveTarget(null);
      await load();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to approve recruiter request';
      toast.error(message);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeny = async (row: RecruiterRequestRow) => {
    const reason = window.prompt('Optional reason for denial:') || '';
    setIsActionLoading(true);
    try {
      await denyRecruiterRequestAdmin(row._id, reason);
      toast.success('Recruiter request denied');
      await load();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to deny recruiter request';
      toast.error(message);
    } finally {
      setIsActionLoading(false);
    }
  };

  const familyMembers = selectedRecruiter?.familyMembers
    ? Object.values(selectedRecruiter.familyMembers as Record<string, any>).filter(Boolean)
    : [];
  const references = selectedRecruiter?.references
    ? Object.values(selectedRecruiter.references as Record<string, any>).filter(Boolean)
    : [];
  const imageDocuments = selectedRecruiter
    ? [
        { label: 'Profile Photo', url: selectedRecruiter.newIdentityProof?.photo },
        { label: 'Aadhaar Front', url: selectedRecruiter.newIdentityProof?.aadharFront },
        { label: 'Aadhaar Back', url: selectedRecruiter.newIdentityProof?.aadharBack },
        { label: 'PAN Front', url: selectedRecruiter.newIdentityProof?.panFront },
        { label: 'PAN Back', url: selectedRecruiter.newIdentityProof?.panBack },
      ].filter((item) => Boolean(item.url && item.url.trim()))
    : [];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Recruiter requests</h1>
        <p className="text-sm text-gray-600">
          List of recruiter onboarding requests submitted from the public form.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No recruiter requests found.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Name</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Email</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Phone</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Recruiter Type</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Updated</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr
                  key={row._id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setSelectedRecruiter(row)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {row.personalDetails?.fullName || '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{row.personalDetails?.email || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">{row.personalDetails?.phone || '-'}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {row.personalDetails?.recruiterType || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      {row.approvalStatus || 'PENDING'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatDate(row.updatedDateTime)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="rounded bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          openApprovePopup(row);
                        }}
                        disabled={isActionLoading}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className="rounded bg-white px-3 py-1.5 text-xs font-medium text-red-700 ring-1 ring-red-200 hover:bg-red-50 disabled:opacity-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDeny(row);
                        }}
                        disabled={isActionLoading}
                      >
                        Deny
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages} ({pagination.totalResults} total)
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={pagination.page === 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedRecruiter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Recruiter details</h2>
              <button
                type="button"
                className="rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setSelectedRecruiter(null)}
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded border border-gray-200 p-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Personal details</h3>
                <p className="text-sm text-gray-700"><span className="font-medium">Name:</span> {renderValue(selectedRecruiter.personalDetails?.fullName)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Email:</span> {renderValue(selectedRecruiter.personalDetails?.email)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Phone:</span> {renderValue(selectedRecruiter.personalDetails?.phone)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">WhatsApp:</span> {renderValue(selectedRecruiter.personalDetails?.wtspNum)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Recruiter Type:</span> {renderValue(selectedRecruiter.personalDetails?.recruiterType)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Role:</span> {renderValue(selectedRecruiter.personalDetails?.role)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Apply For:</span> {renderValue(selectedRecruiter.personalDetails?.applyFor)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Gender:</span> {renderValue(selectedRecruiter.personalDetails?.gender)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">DOB:</span> {renderValue(selectedRecruiter.personalDetails?.dob)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Current City/State:</span> {renderValue(selectedRecruiter.personalDetails?.currCity)} / {renderValue(selectedRecruiter.personalDetails?.currState)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Permanent City/State:</span> {renderValue(selectedRecruiter.personalDetails?.permCity)} / {renderValue(selectedRecruiter.personalDetails?.permState)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Languages:</span> {selectedRecruiter.personalDetails?.languages?.join(', ') || '-'}</p>
              </div>

              <div className="rounded border border-gray-200 p-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Professional details</h3>
                <p className="text-sm text-gray-700"><span className="font-medium">Highest Qualification:</span> {renderValue(selectedRecruiter.qualification?.highestQualification)}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Hiring Dept:</span> {selectedRecruiter.hiringDept?.join(', ') || '-'}</p>
                <p className="text-sm text-gray-700"><span className="font-medium">Submitted At:</span> {formatDate(selectedRecruiter.updatedDateTime)}</p>
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-900">Work experience</p>
                  {selectedRecruiter.qualification?.workExperience?.length ? (
                    <ul className="mt-1 list-disc pl-5 text-sm text-gray-700">
                      {selectedRecruiter.qualification.workExperience.map((item, index) => (
                        <li key={`${item.CompanyName}-${index}`}>
                          {renderValue(item.CompanyName)} - {renderValue(item.ExperienceYears)} years
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-700">-</p>
                  )}
                </div>
              </div>

              <div className="rounded border border-gray-200 p-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Family members</h3>
                {familyMembers.length ? (
                  <ul className="space-y-2 text-sm text-gray-700">
                    {familyMembers.map((member: any, index: number) => (
                      <li key={`${member?.name || 'member'}-${index}`}>
                        <span className="font-medium">{renderValue(member?.name)}</span> - {renderValue(member?.relationship)} ({renderValue(member?.age)})
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-700">-</p>
                )}
              </div>

              <div className="rounded border border-gray-200 p-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-900">References</h3>
                {references.length ? (
                  <ul className="space-y-2 text-sm text-gray-700">
                    {references.map((ref: any, index: number) => (
                      <li key={`${ref?.name || 'ref'}-${index}`}>
                        <span className="font-medium">{renderValue(ref?.name)}</span> - {renderValue(ref?.designation)} ({renderValue(ref?.phone)})
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-700">-</p>
                )}
              </div>
            </div>

            <div className="mt-4 rounded border border-gray-200 p-4">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Documents</h3>
              {imageDocuments.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {imageDocuments.map((doc) => (
                    <a
                      key={doc.label}
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded border border-gray-200 p-2 hover:bg-gray-50"
                    >
                      <p className="mb-2 text-xs font-medium text-gray-700">{doc.label}</p>
                      <img
                        src={doc.url}
                        alt={doc.label}
                        className="h-36 w-full rounded object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-700">No images available.</p>
              )}
            </div>

            <div className="mt-4 rounded border border-gray-200 p-4">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Questionnaire</h3>
              {selectedRecruiter.about?.questions?.length ? (
                <div className="space-y-2">
                  {selectedRecruiter.about.questions.map((item, index) => (
                    <div key={`${item.question}-${index}`} className="text-sm text-gray-700">
                      <p className="font-medium text-gray-900">{item.question || '-'}</p>
                      <p>
                        {Array.isArray(item.answer)
                          ? item.answer.join(', ')
                          : renderValue(item.answer as string | undefined)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-700">-</p>
              )}
            </div>
          </div>
        </div>
      )}

      {approveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Create User & Approve</h2>
              <button
                type="button"
                className="rounded px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setApproveTarget(null)}
                disabled={isActionLoading}
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Username</label>
                <input
                  className="h-10 w-full rounded border border-gray-300 px-3 text-sm"
                  value={approveForm.username}
                  onChange={(e) => setApproveForm((prev) => ({ ...prev, username: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                <input
                  className="h-10 w-full rounded border border-gray-300 px-3 text-sm"
                  type="email"
                  value={approveForm.email}
                  onChange={(e) => setApproveForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
                <input
                  className="h-10 w-full rounded border border-gray-300 px-3 text-sm"
                  value={approveForm.phone}
                  maxLength={10}
                  onChange={(e) =>
                    setApproveForm((prev) => ({ ...prev, phone: e.target.value.replace(/[^0-9]/g, '') }))
                  }
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Password</label>
                <div className="relative">
                  <input
                    className="h-10 w-full rounded border border-gray-300 px-3 pr-10 text-sm"
                    type={showPassword ? 'text' : 'password'}
                    value={approveForm.password}
                    onChange={(e) => setApproveForm((prev) => ({ ...prev, password: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-2 text-gray-500"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
                <select
                  className="h-10 w-full rounded border border-gray-300 px-3 text-sm"
                  value={approveForm.role}
                  onChange={(e) =>
                    setApproveForm((prev) => ({
                      ...prev,
                      role: e.target.value,
                      assignedManagerUserId: '',
                    }))
                  }
                >
                  <option value="">Select role</option>
                  {[
                    'ADMIN',
                    'FBDE',
                    'CONSULTANT',
                    'MODERATOR',
                    'SHM',
                    'HM',
                    'AGENCY',
                    'HR',
                    'BDE',
                    'TPO',
                    'FREELANCE_COUNSELLOR',
                    'MASTER_BDE',
                    'FREELANCE_BDE',
                    'BDE_INTERN',
                    'INTERNAL_TEAM',
                    'COMPANY',
                  ].map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Location</label>
                <input
                  className="h-10 w-full rounded border border-gray-300 px-3 text-sm"
                  value={approveForm.location}
                  onChange={(e) => setApproveForm((prev) => ({ ...prev, location: e.target.value }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Assigned Manager</label>
                <select
                  className="h-10 w-full rounded border border-gray-300 px-3 text-sm"
                  value={approveForm.assignedManagerUserId}
                  onChange={(e) =>
                    setApproveForm((prev) => ({ ...prev, assignedManagerUserId: e.target.value }))
                  }
                  disabled={managerLoading}
                >
                  <option value="">{managerLoading ? 'Loading managers...' : 'Select manager'}</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.username} {manager.email ? `(${manager.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700"
                onClick={() => setApproveTarget(null)}
                disabled={isActionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                onClick={() => void handleApproveSubmit()}
                disabled={isActionLoading}
              >
                {isActionLoading ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruiterRequestsPage;
